import React from 'react'
import TextField from '@material-ui/core/TextField'
import { makeStyles } from '@material-ui/core/styles'
import styles from '../../styles'
import { FieldRenderProps } from 'react-final-form'
import classNames from 'classnames'
import { ClassValue } from 'classnames/types'
import { FormattedMessageModel } from '../../types/fields/fieldsModel'
import { useIntl } from 'react-intl'
import { useMediaQuery } from 'react-responsive'
import { InputAdornment } from "@material-ui/core";

const useStyles = makeStyles(
	(theme) => (
		{
			...styles(theme),
			textFieldStyle: {
				width: '100%',
				padding: '8px 8px 8px 0px',
				boxSizing: 'border-box'
			},
			textFieldLargeScreenStyle: {
				'& .MuiInput-formControl': {
					'& .MuiInput-input': {
						fontSize: 20,
						fontWeight: 500,
						color: theme.palette.blue.main
					}
				}
			},
			textFieldSmallScreenStyle: {
				'& .MuiInput-formControl': {
					'& .MuiInput-input': {
						fontSize: 14,
						fontWeight: 500,
						color: theme.palette.blue.main
					}
				}
			},
			errorStyle: {
				color: '#B92D00',
				fontSize: 10
			},
			readOnlyStyle: {
				'& .MuiInput-input': {
					cursor: 'default'
				},
				// Champ non modifiable : aucune réaction au survol ni au focus, le soulignement reste identique
				'& .MuiInput-underline:before, & .MuiInput-underline:hover:not(.Mui-disabled):before': {
					borderBottom: '1px dotted rgba(0, 0, 0, 0.26)'
				},
				'& .MuiInput-underline:after': {
					borderBottom: 'none'
				}
			},
			errorColor: {
				color: '#B92D00 !important'
			},
			errorBorder: {
				border: '1px solid #B92D00'
			}
		}
	)
)

interface AcceptingProps {
	id: string,
	label?: string,
	placeholder?: FormattedMessageModel,
	multiline?: boolean
	maxLength?: number,
	type?: string,
	disablePaste: boolean,
	adornment?: string,
	readOnly?: boolean
}

type FormTextFieldProps =
	FieldRenderProps<string>
	& AcceptingProps

const FormTextField: React.FC<FormTextFieldProps> = (
	{
		input,
		meta: {
			touched,
			error
		},
		id,
		label,
		placeholder,
		multiline,
		maxLength,
		type = 'text',
		disablePaste = false,
		adornment,
		readOnly = false
	}
) => {
	const classes = useStyles()
	const intl = useIntl()
	const isLargeScreen = useMediaQuery(
		{
			query: '(min-width: 1025px)'
		}
	)

	const labelClass: ClassValue = classNames(
		classes.text5,
		classes.colorBlue,
		{
			[classes.errorColor]: touched && error
		}
	)

	const textFieldClass: ClassValue = classNames(
		classes.textFieldStyle,
		{
			[classes.textFieldLargeScreenStyle]: isLargeScreen,
			[classes.textFieldSmallScreenStyle]: !isLargeScreen,
			[classes.errorBorder]: touched && error,
			[classes.readOnlyStyle]: readOnly
		}
	)

	// Gestion d'un éventuel adornment
	const startAdornment = adornment ? { startAdornment: <InputAdornment position="start">{adornment}</InputAdornment> } : {}

	return (
		<>
			<span
				className={labelClass}
			>
				{label}
			</span>

			<TextField
				id={id}
				color="primary"
				className={textFieldClass}
				multiline={multiline}
				inputProps={{ maxLength: maxLength }}
				placeholder={placeholder ?
					intl.formatMessage(
						{
							id: placeholder.id,
							defaultMessage: placeholder.defaultMessage,
							description: placeholder.description
						}
					) : ''
				}
				onPaste={(e) => disablePaste && e.preventDefault()}
				InputProps={{
					...startAdornment,
					readOnly
				}}
				{...input}
			/>

			{
				touched &&
				((error && <span className={classes.errorStyle}>{error}</span>))
			}
		</>
	)
}

export default FormTextField
